<template>
  <div class="mines-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.mines.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.mines.subtitle') }}</p>
    </div>

    <!-- Grid size selector -->
    <div class="grid-size-tabs">
      <button
        v-for="size in GRID_SIZES"
        :key="size"
        type="button"
        class="grid-tab"
        :class="{ active: selectedGridSize === size }"
        @click="selectGridSize(size)"
      >
        {{ gridSizeLabel(size) }}
      </button>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <form v-else class="settings-form" @submit.prevent="saveConfig">

      <!-- House Edge -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_house_edge') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_house_edge') }}
            <input
              v-model.number="form.house_edge_pct"
              type="number"
              step="0.5"
              min="1"
              max="10"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_house_edge') }}</span>
          </label>
          <div class="rtp-display">
            <span class="rtp-label">{{ $t('admin.config.label_rtp') }}</span>
            <span class="rtp-value">{{ rtpDisplay }}%</span>
          </div>
        </div>
      </section>

      <!-- Mine Count Range -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.mines.section_mine_count') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.mines.label_min_mines') }}
            <input
              v-model.number="form.min_mines"
              type="number"
              step="1"
              :min="1"
              :max="form.max_mines"
              class="form-input"
              required
            />
            <span class="form-hint">Minimum mines a player can choose (min 1)</span>
          </label>
          <label class="form-label">
            {{ $t('admin.config.mines.label_max_mines') }}
            <input
              v-model.number="form.max_mines"
              type="number"
              step="1"
              :min="form.min_mines"
              :max="maxAllowedMines"
              class="form-input"
              required
            />
            <span class="form-hint">Maximum mines allowed (grid size − 1 = {{ maxAllowedMines }})</span>
          </label>
        </div>
      </section>

      <!-- Bet Limits -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_min_bet') }}
            <input
              v-model="form.min_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.label_max_bet') }}
            <input
              v-model="form.max_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
        </div>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_max_payout_simple') }}
            <input
              v-model="form.max_payout"
              type="number"
              step="0.01"
              min="0"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_no_cap_0') }}</span>
          </label>
        </div>
      </section>

      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
      <div v-if="saveSuccess" class="success-banner">{{ saveSuccess }}</div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
        </button>
        <button type="button" class="btn btn-secondary" :disabled="saving || loading" @click="resetForm">
          {{ $t('admin.config.reset') }}
        </button>
        <span v-if="sourceLabel" class="source-badge" :class="sourceClass">
          {{ sourceLabel }}
        </span>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

const GRID_SIZES = [9, 16, 25, 49] as const
type GridSize = typeof GRID_SIZES[number]

interface MinesConfigResponse {
  source: 'database' | 'default'
  grid_size: number
  house_edge_pct: number
  min_bet: string
  max_bet: string
  max_payout: string
  min_mines: number
  max_mines: number
  rtp: number
  available_grid_sizes: number[]
}

const { t } = useI18n()

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveSuccess = ref('')

const source = ref<'database' | 'default'>('default')
const selectedGridSize = ref<GridSize>(25)

const form = reactive({
  house_edge_pct: 1.0,
  min_bet: '1.00',
  max_bet: '10000.00',
  max_payout: '0.00',
  min_mines: 1,
  max_mines: 24,
})

const maxAllowedMines = computed(() => selectedGridSize.value - 1)

const rtpDisplay = computed(() => (100 - form.house_edge_pct).toFixed(2))

const sourceLabel = computed(() => (source.value === 'database' ? t('admin.config.saved_to_db') : t('admin.config.using_defaults')))
const sourceClass = computed(() => (source.value === 'database' ? 'source-db' : 'source-default'))

function gridSizeLabel(size: GridSize): string {
  const side = Math.round(Math.sqrt(size))
  return `${side}×${side}`
}

async function loadConfig(gridSize: GridSize = selectedGridSize.value) {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<MinesConfigResponse>('/admin/api/games/mines/config', {
      params: { grid_size: gridSize },
    })
    populateForm(res.data)
  } catch {
    loadError.value = t('admin.config.mines.error_load')
  } finally {
    loading.value = false
  }
}

function populateForm(data: MinesConfigResponse) {
  source.value = data.source
  form.house_edge_pct = data.house_edge_pct
  form.min_bet = data.min_bet
  form.max_bet = data.max_bet
  form.max_payout = data.max_payout
  form.min_mines = data.min_mines
  form.max_mines = data.max_mines
}

async function selectGridSize(size: GridSize) {
  selectedGridSize.value = size
  await loadConfig(size)
}

function resetForm() {
  loadConfig(selectedGridSize.value)
}

async function saveConfig() {
  saveError.value = ''
  saveSuccess.value = ''
  saving.value = true

  try {
    const res = await apiClient.post<MinesConfigResponse>('/admin/api/games/mines/config', {
      grid_size: selectedGridSize.value,
      house_edge_pct: form.house_edge_pct,
      min_bet: form.min_bet,
      max_bet: form.max_bet,
      max_payout: form.max_payout,
      min_mines: form.min_mines,
      max_mines: form.max_mines,
    })
    populateForm(res.data)
    saveSuccess.value = t('admin.config.mines.save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.mines.error_save')
  } finally {
    saving.value = false
  }
}

onMounted(() => loadConfig())
</script>

<style scoped>
.mines-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 20px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Grid size tabs */
.grid-size-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.grid-tab {
  padding: 7px 16px;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--border, #2d4a5a);
  background: var(--bg-secondary, #213743);
  color: var(--text-secondary, #b1bad3);
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.grid-tab:hover {
  background: var(--bg-hover, #2a4555);
  color: var(--text-primary, #fff);
}

.grid-tab.active {
  background: var(--accent, #1a9e6e);
  color: #fff;
  border-color: var(--accent, #1a9e6e);
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* Form */
.settings-form {
  max-width: 680px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: flex-start;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

/* RTP display inline with house edge */
.rtp-display {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding-top: 18px;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.rtp-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-success-light, #34d399);
}

/* Actions row */
.form-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}

/* Source badge */
.source-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}

.btn-secondary {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  color: var(--text-secondary, #b1bad3);
}

.btn-secondary:hover:not(:disabled) {
  color: var(--text-primary, #fff);
}
</style>
